// src/components/layout/Footer.jsx
import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faLocationDot,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp, faInstagram } from "@fortawesome/free-brands-svg-icons";
import contactData from "../../data/contactData";

const FooterWrap = styled.footer`
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing["3xl"]} 0 0;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 1rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 3rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const BrandCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const LogoWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const LogoImage = styled.img`
  width: 52px;
  height: 52px;
  object-fit: contain;
  display: block;
  border-radius: 35px;
`;

const LogoName = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-weight: 700;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.1;
`;

const LogoSub = styled.div`
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  color: ${({ theme }) => theme.colors.gold};
  text-transform: uppercase;
`;

const BrandDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  max-width: 320px;
  margin-bottom: 1.5rem;
`;

const SocialRow = styled.div`
  display: flex;
  gap: 0.75rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    order: 3;
    margin-top: 1rem;
  }
`;

const SocialBtn = styled.a`
  width: 38px;
  height: 38px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.9rem;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-decoration: none;

  &:hover {
    border-color: ${({ theme }) => theme.colors.gold};
    color: ${({ theme }) => theme.colors.gold};
    background: ${({ theme }) => theme.colors.goldMuted};
    transform: translateY(-2px);
  }
`;

const ActionBtns = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    order: 4;
    margin-top: 0.75rem;
  }
`;

const ActionBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  border-radius: ${({ theme }) => theme.borderRadius.button};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.normal};
  cursor: pointer;

  ${({ $primary, theme }) =>
    $primary
      ? `background: linear-gradient(135deg, ${theme.colors.gold}, ${theme.colors.goldLight}); color: #1a1000;`
      : `background: transparent; color: ${theme.colors.textSecondary}; border: 1px solid ${theme.colors.border};`}

  &:hover {
    transform: translateY(-2px);
    ${({ $primary, theme }) =>
      $primary
        ? `box-shadow: ${theme.shadows.gold};`
        : `border-color: ${theme.colors.gold}; color: ${theme.colors.gold};`}
  }
`;

const ColTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.gold};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 1.25rem;
`;

const NavList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const NavItem = styled.li``;

const NavLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: "—";
    color: ${({ theme }) => theme.colors.gold};
    font-size: 0.7rem;
    opacity: 0;
    transform: translateX(-4px);
    transition: all ${({ theme }) => theme.transitions.fast};
    display: inline-block;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};

    &::before {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const ContactList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
`;

const ContactItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};

  a {
    color: inherit;
    text-decoration: none;
    transition: color ${({ theme }) => theme.transitions.fast};

    &:hover {
      color: ${({ theme }) => theme.colors.gold};
    }
  }
`;

const ContactIcon = styled.span`
  color: ${({ theme }) => theme.colors.gold};
  font-size: 0.85rem;
  margin-top: 2px;
  flex-shrink: 0;
`;

const AddressBlock = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: 2.5rem 0 0;
`;

const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 0;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ServiceTag = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Footer = () => (
  <FooterWrap>
    <Container>
      <Grid>
        <BrandCol>
          <LogoWrap>
            <LogoImage src="/logo-image.png" alt="Metrocrafts Interiors logo" />
            <div>
              <LogoName>Metrocrafts</LogoName>
              <LogoSub>Interiors</LogoSub>
            </div>
          </LogoWrap>
          <BrandDesc>
            Premium interior craftsmanship for homes and offices across
            Hyderabad &amp; Telangana. Carpentry, electrical, tiles, ceilings
            and painting — executed with precision.
          </BrandDesc>
          <SocialRow>
            <SocialBtn
              href={contactData.instagramHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <FontAwesomeIcon icon={faInstagram} />
            </SocialBtn>
            <SocialBtn
              href={contactData.websiteHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Website"
            >
              <FontAwesomeIcon icon={faGlobe} />
            </SocialBtn>
            <SocialBtn
              href={contactData.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
            >
              <FontAwesomeIcon icon={faWhatsapp} />
            </SocialBtn>
          </SocialRow>
          <ActionBtns>
            <ActionBtn href={contactData.phoneHref} $primary>
              <FontAwesomeIcon icon={faPhone} /> Call
            </ActionBtn>
            <ActionBtn
              href={contactData.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp
            </ActionBtn>
          </ActionBtns>
        </BrandCol>

        <div>
          <ColTitle>Explore</ColTitle>
          <NavList>
            {[
              { label: "Home", to: "/" },
              { label: "Services", to: "/services" },
              { label: "Gallery", to: "/gallery" },
              { label: "About & Contact", to: "/about" },
              { label: "Careers", to: "/careers" },
            ].map((l) => (
              <NavItem key={l.to}>
                <NavLink to={l.to}>{l.label}</NavLink>
              </NavItem>
            ))}
          </NavList>
        </div>

        <div>
          <ColTitle>Reach Us</ColTitle>
          <ContactList>
            <ContactItem>
              <ContactIcon>
                <FontAwesomeIcon icon={faPhone} />
              </ContactIcon>
              <a href={contactData.phoneHref}>{contactData.phoneDisplay}</a>
            </ContactItem>
            <ContactItem>
              <ContactIcon>
                <FontAwesomeIcon icon={faEnvelope} />
              </ContactIcon>
              <a href={contactData.emailHref}>{contactData.email}</a>
            </ContactItem>
            <ContactItem>
              <ContactIcon>
                <FontAwesomeIcon icon={faGlobe} />
              </ContactIcon>
              <a
                href={contactData.websiteHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {contactData.websiteDisplay}
              </a>
            </ContactItem>
            <ContactItem>
              <ContactIcon>
                <FontAwesomeIcon icon={faInstagram} />
              </ContactIcon>
              <a
                href={contactData.instagramHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                @{contactData.instagramHandle}
              </a>
            </ContactItem>
            <ContactItem>
              <ContactIcon>
                <FontAwesomeIcon icon={faLocationDot} />
              </ContactIcon>
              <AddressBlock>
                {contactData.addressLines.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </AddressBlock>
            </ContactItem>
          </ContactList>
        </div>
      </Grid>

      <Divider />
      <Bottom>
        <Copyright>
          All rights reserved. © 2025 by Metro Crafts Interiors
        </Copyright>
        <ServiceTag>
          Powered and secured by Kommu Tech &amp; Marketing Pvt.Ltd
        </ServiceTag>
      </Bottom>
    </Container>
  </FooterWrap>
);

export default Footer;
